<!--
  BEWARE: This page has many similarities with the ArticleDetail.vue and the AnimalPageDetail.vue template,
  changes for this template may affect the ArticleDetail.vue and the AnimalPageDetail.vue template as well.
-->

<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Breadcrumbs -->
      <OrganismNavigationBreadcrumb class="mx-auto p-4 md:container md:mb-16 md:pb-0 md:pt-10 lg:mb-20" :path="breadcrumbs" />

      <!-- Header with title, subtitle, introduction (and no image) -->
      <MoleculeHeaderOverview class="mb-8 mt-1 md:mb-12 lg:mb-16 lg:mt-20" :title="page.title" :subtitle="page.subtitle" :introduction="page.introduction" />

      <!--
        Section with container and grid for page content.
        [1] The conditional extra padding-top is for when there is a page image
        [2] The negative margin-bottom on the next column is needed
            to pull up all the content items (if they exist!) a bit to fake the layout that they
            are all together in 1 white background div with rounded corners
            (secret: this is NOT the case!)
      -->
      <section
        id="detail-page-content"
        class="container relative grid grid-cols-12 mx-auto px-4"
        :class="{
          'page-image-container': page.image,
          'margin-bottom-page-content-with-content-items-following': page.matrix.length,
          'margin-bottom-page-content-without-content-items-following': !page.matrix.length && !page.references.length,
        }"
      >
        <!-- Div with responsive page layout in columns -->
        <div v-if="page.image || page.description" class="relative z-40 col-span-full 2xl:col-end-10 2xl:col-start-4 lg:col-end-12 lg:col-start-2 xl:col-end-11 xl:col-start-3">
          <!-- White sheet with page content -->
          <div
            class="padding-page-content relative bg-white rounded-t-xl"
            :class="{ 'rounded-b-xl': page.matrix.length || !page.references.length, 'rounded-b-none': !page.matrix.length }"
          >
            <!-- Page image with a negative margin defined in a separate css class at the bottom of this file with a corresponding css class at its parent that contains extra padding -->
            <figure
              v-if="page.image"
              class="page-image relative"
              :class="{
                'margin-bottom-page-content-without-content-items-following': page.description,
              }"
            >
              <MoleculePicture class="block w-full rounded-xl overflow-hidden" :image="page.image" :alt="page.title" />
            </figure>

            <!-- Page body text -->
            <div v-if="page.description" class="prose prose--app max-w-none overflow-auto" v-html="page.description" />
          </div>
        </div>
      </section>

      <!-- Possible content items -->
      <OrganismMatrix
        v-if="page.matrix.length"
        :enable-last-item-rounded="page.references.length > 0"
        :items="page.matrix"
        :expiry-date="page.expiryDate || null"
        :class="{ 'margin-bottom-page-content-without-content-items-following': !page.references.length }"
      />

      <!-- Possible references to other pages -->
      <MoleculePageDetailReferences id="detail-page-references" class="margin-bottom-page-content-without-content-items-following" :references="page.references" />

      <section v-if="page.donationReference && page.donationReference.url" id="detail-page-donation" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="relative">
          <MoleculePicture :image="page.donationReference.image" class="cover-fill hidden w-full h-full lg:block" />
          <div class="bottom-0 right-0 w-full lg:absolute lg:max-w-xl">
            <MoleculeDonation
              :title="page.donationReference.title"
              :subtitle="page.donationReference.subtitle"
              :introduction="page.donationReference.introduction"
              :image="page.donationReference.image"
              :url="page.donationReference.url"
              :price1="page.donationReference.donationPrice1"
              :price2="page.donationReference.donationPrice2"
              :price3="page.donationReference.donationPrice3"
            />
          </div>
        </div>
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
interface Page {
  title: string
  subtitle: string
  introduction: string
  image?: string
  description?: string
  matrix: any[] // Adjust type based on actual structure
  references: any[] // Adjust type based on actual structure
  donationReference?: {
    url: string
    image: string
    title: string
    subtitle: string
    introduction: string
    donationPrice1?: number
    donationPrice2?: number
    donationPrice3?: number
  }
}

interface Props {
  breadcrumbs: any[] // Adjust type based on actual structure
  page: Page
}

defineProps<Props>()
</script>

<style lang="scss" scoped>
.page-image-container {
  @apply pt-8;

  @screen md {
    @apply pt-12;
  }

  @screen lg {
    @apply pt-20;
  }
}

.page-image {
  @apply -mt-16;

  @screen md {
    @apply -mt-20;
  }

  @screen lg {
    @apply -mt-32;
  }
}
</style>
